import { normalizeBallNum, splitCsv, normalizeApiTone, waveTone } from '@/utils/ballUtils'
import { formatDrawDateTime, parseOpenTimeToMs, nextDrawDeadlineFromLastMs, issuePlusOne } from '@/utils/dateUtils'

export function openStyleSlots7(val) {
  const out = [null, null, null, null, null, null, null]
  if (val == null) return out
  if (Array.isArray(val)) {
    for (let i = 0; i < 7 && i < val.length; i++) out[i] = val[i]
    return out
  }
  const parts = String(val).split(/[,，]/)
  for (let i = 0; i < 7 && i < parts.length; i++) {
    const t = parts[i].trim()
    out[i] = t === '' ? null : t
  }
  return out
}

export function slotHasOpenValue(v) {
  if (v == null) return false
  if (typeof v === 'number' && !Number.isNaN(v)) return true
  const s = String(v).trim()
  if (s === '' || s.toLowerCase() === 'null') return false
  return true
}

export function hasOpenStyleDataForFill(raw) {
  if (!raw || typeof raw !== 'object') return false
  const codes = openStyleSlots7(raw.openCode ?? raw.opencode)
  const zods = openStyleSlots7(raw.zodiac)
  return codes.some(s => slotHasOpenValue(s)) && zods.some(s => slotHasOpenValue(s))
}

export function isOpenStyleResultComplete(raw) {
  if (!raw || typeof raw !== 'object') return false
  const codes = openStyleSlots7(raw.openCode ?? raw.opencode)
  const waves = openStyleSlots7(raw.wave ?? raw.Wave ?? '')
  const zods = openStyleSlots7(raw.zodiac)
  for (let i = 0; i < 7; i++) {
    if (!slotHasOpenValue(codes[i])) return false
    if (!slotHasOpenValue(waves[i])) return false
    if (!slotHasOpenValue(zods[i])) return false
  }
  return true
}

function rollDeadlineToFuture(ms) {
  if (ms == null || !Number.isFinite(ms)) return null
  if (ms > Date.now()) return ms
  if (Date.now() - ms <= 10 * 60 * 1000) return ms
  return ms + 24 * 60 * 60 * 1000
}

export function buildDrawFromOpenStyle(raw, drawSchedule, prevDraw, waveTables, opts = {}) {
  const useOpenTimeAsDeadline = opts.useOpenTimeAsDeadline === true
  const canBuildLiveDeadline =
    useOpenTimeAsDeadline &&
    raw &&
    typeof raw === 'object' &&
    (raw.openCode != null || raw.opencode != null) &&
    (raw.issueNo != null || raw.expect != null || raw.openTime != null)
  if (!hasOpenStyleDataForFill(raw) && !canBuildLiveDeadline) return null
  const blankUnrevealed = opts.afterDeadline === true || useOpenTimeAsDeadline
  const openStyleComplete = opts.openStyleComplete === true
  const deadlineFrom = ms => nextDrawDeadlineFromLastMs(ms, drawSchedule)

  const code = raw.openCode ?? raw.opencode
  const numSlots = openStyleSlots7(code)
  const waveSlots = openStyleSlots7(raw.wave ?? raw.Wave ?? '')
  const zodiacSlots = openStyleSlots7(raw.zodiac)

  const toneForIndex = i => {
    if (!slotHasOpenValue(waveSlots[i])) return ''
    const t = normalizeApiTone(waveSlots[i])
    return t || ''
  }

  const prevMain =
    prevDraw && Array.isArray(prevDraw.mainNumbers) ? prevDraw.mainNumbers : []
  const prevSp =
    prevDraw && prevDraw.special
      ? prevDraw.special
      : { value: '', zodiac: '', element: '', tone: '' }

  const mainNumbers = []
  for (let i = 0; i < 6; i++) {
    if (slotHasOpenValue(numSlots[i])) {
      const pv = normalizeBallNum(numSlots[i])
      const t = toneForIndex(i)
      mainNumbers.push({
        value: pv,
        zodiac: slotHasOpenValue(zodiacSlots[i]) ? String(zodiacSlots[i]) : '',
        element: '',
        tone: t || waveTone(pv, waveTables)
      })
    } else if (blankUnrevealed) {
      mainNumbers.push({ value: '', zodiac: '', element: '', tone: '' })
    } else {
      const pm = prevMain[i]
      mainNumbers.push(
        pm
          ? { value: pm.value, zodiac: pm.zodiac, element: pm.element || '', tone: pm.tone || '' }
          : { value: '', zodiac: '', element: '', tone: '' }
      )
    }
  }

  let special
  if (slotHasOpenValue(numSlots[6])) {
    const pv = normalizeBallNum(numSlots[6])
    const t = toneForIndex(6)
    special = {
      value: pv,
      zodiac: slotHasOpenValue(zodiacSlots[6]) ? String(zodiacSlots[6]) : '',
      element: '',
      tone: t || waveTone(pv, waveTables)
    }
  } else if (blankUnrevealed) {
    special = { value: '', zodiac: '', element: '', tone: '' }
  } else {
    special = {
      value: prevSp.value,
      zodiac: prevSp.zodiac,
      element: prevSp.element || '',
      tone: prevSp.tone || ''
    }
  }

  const issueStr =
    raw.issueNo != null && String(raw.issueNo).trim() !== ''
      ? String(raw.issueNo)
      : raw.expect != null && String(raw.expect).trim() !== ''
        ? String(raw.expect)
        : prevDraw && prevDraw.issue != null
          ? String(prevDraw.issue)
          : ''
  const drawTimeFmt = raw.openTime
    ? formatDrawDateTime(raw.openTime)
    : prevDraw && prevDraw.drawTimeFormatted
      ? prevDraw.drawTimeFormatted
      : ''

  let nextDeadline =
    prevDraw && prevDraw.nextDrawDeadlineMs != null
      ? prevDraw.nextDrawDeadlineMs
      : null
  if (raw.openTime) {
    const ms = parseOpenTimeToMs(raw.openTime)
    if (ms != null) {
      if (useOpenTimeAsDeadline) {
        nextDeadline = openStyleComplete ? ms + 24 * 60 * 60 * 1000 : rollDeadlineToFuture(ms)
      } else if (!blankUnrevealed || openStyleComplete) {
        nextDeadline = deadlineFrom(ms)
      }
    }
  }

  const nextIssueDisplay = useOpenTimeAsDeadline
    ? (issueStr || (prevDraw && prevDraw.issue) || '—')
    : (issueStr ? issuePlusOne(issueStr) : '—')

  return {
    issue: issueStr || (prevDraw && prevDraw.issue) || '',
    drawTimeFormatted: drawTimeFmt,
    nextIssueDisplay,
    nextDrawDeadlineMs: nextDeadline,
    nextIssue: '—',
    nextTime: '—',
    mainNumbers,
    special,
    revealed: numSlots.map(slotHasOpenValue)
  }
}

export function mapHkLotteryApiToDraw(payload, waveTables) {
  if (!payload || typeof payload !== 'object') return null
  const parts = Array.isArray(payload.body) ? payload.body : null
  if (!parts || parts.length < 9) return null

  const issue = parts[0]
  const ballSlots = parts.slice(1, 8)
  const nextIssue = parts[8] || ''

  const revealed = ballSlots.map(s => {
    const n = parseInt(s, 10)
    return !isNaN(n) && n >= 0 && n <= 49
  })

  const mainNumbers = ballSlots.slice(0, 6).map((s, i) =>
    revealed[i]
      ? { value: normalizeBallNum(s), zodiac: '', element: '', tone: waveTone(s, waveTables) }
      : { value: '', zodiac: '', element: '', tone: '' }
  )
  const special = revealed[6]
    ? { value: normalizeBallNum(ballSlots[6]), zodiac: '', element: '', tone: waveTone(ballSlots[6], waveTables) }
    : { value: '', zodiac: '', element: '', tone: '' }

  let drawTimeFormatted = ''
  let nextDrawDeadlineMs = null
  if (parts.length >= 13) {
    const drawTimeStr = parts[12]
    const timeMatch = drawTimeStr && drawTimeStr.match(/(\d+)点(\d+)分/)
    if (timeMatch) {
      const drawHour = parseInt(timeMatch[1], 10)
      const drawMin = parseInt(timeMatch[2], 10)
      const now = new Date()
      const drawDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), drawHour, drawMin, 0, 0)
      drawTimeFormatted = formatDrawDateTime(drawDate)

      const nextMonth = parseInt(parts[9], 10)
      const nextDay = parseInt(parts[10], 10)
      if (!isNaN(nextMonth) && !isNaN(nextDay) && nextMonth >= 1 && nextMonth <= 12 && nextDay >= 1 && nextDay <= 31) {
        const yr = now.getFullYear()
        const nextDate = new Date(yr, nextMonth - 1, nextDay, drawHour, drawMin, 0, 0)
        while (nextDate.getTime() <= Date.now()) {
          nextDate.setDate(nextDate.getDate() + 1)
        }
        nextDrawDeadlineMs = nextDate.getTime()
      }
    }
  }

  return {
    issue,
    drawTimeFormatted,
    nextIssueDisplay: nextIssue || issuePlusOne(issue),
    nextDrawDeadlineMs,
    nextIssue,
    nextTime: '—',
    mainNumbers,
    special,
    revealed
  }
}

export function mapYuexiawangApiToDraw(payload, waveTables) {
  if (!payload || typeof payload !== 'object') return null
  const k = String(payload.k != null ? payload.k : '').trim()
  if (!k) return null
  const parts = k.split(',').map(s => s.trim())
  if (parts.length < 9) return null

  const issue = parts[0]
  const ballSlots = parts.slice(1, 8)
  const nextIssue = parts[8] || ''

  const revealed = ballSlots.map(s => {
    const n = parseInt(s, 10)
    return !isNaN(n) && n >= 1 && n <= 49
  })

  const mainNumbers = ballSlots.slice(0, 6).map((s, i) =>
    revealed[i]
      ? { value: normalizeBallNum(s), zodiac: '', element: '', tone: waveTone(s, waveTables) }
      : { value: '', zodiac: '', element: '', tone: '' }
  )
  const special = revealed[6]
    ? { value: normalizeBallNum(ballSlots[6]), zodiac: '', element: '', tone: waveTone(ballSlots[6], waveTables) }
    : { value: '', zodiac: '', element: '', tone: '' }

  let drawTimeFormatted = ''
  let nextDrawDeadlineMs = null
  if (parts.length >= 13) {
    const drawTimeStr = parts[12]
    const timeMatch = drawTimeStr && drawTimeStr.match(/(\d+)点(\d+)分/)
    if (timeMatch) {
      const drawHour = parseInt(timeMatch[1], 10)
      const drawMin = parseInt(timeMatch[2], 10)
      const now = new Date()
      const drawDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), drawHour, drawMin, 0, 0)
      drawTimeFormatted = formatDrawDateTime(drawDate)

      const nextMonth = parseInt(parts[9], 10)
      const nextDay = parseInt(parts[10], 10)
      if (!isNaN(nextMonth) && !isNaN(nextDay) && nextMonth >= 1 && nextMonth <= 12 && nextDay >= 1 && nextDay <= 31) {
        const yr = now.getFullYear()
        const nextDate = new Date(yr, nextMonth - 1, nextDay, drawHour, drawMin, 0, 0)
        if (nextDate.getTime() <= Date.now()) {
          nextDate.setDate(nextDate.getDate() + 1)
        }
        nextDrawDeadlineMs = nextDate.getTime()
      }
    }
  }

  return {
    issue,
    drawTimeFormatted,
    nextIssueDisplay: nextIssue || issuePlusOne(issue),
    nextDrawDeadlineMs,
    nextIssue,
    nextTime: '—',
    mainNumbers,
    special,
    revealed
  }
}

export function mapApiToDraw(raw, drawSchedule) {
  if (!raw || typeof raw !== 'object') return null
  const deadlineFrom = ms => nextDrawDeadlineFromLastMs(ms, drawSchedule)

  if (raw.openCode != null && String(raw.openCode).trim() !== '') {
    const nums = splitCsv(raw.openCode)
    if (nums.length >= 7) {
      const waves = splitCsv(raw.wave)
      const zodiacs = splitCsv(raw.zodiac)
      const toneAt = i => normalizeApiTone(waves[i])
      const mainNumbers = nums.slice(0, 6).map((n, i) => ({
        value: normalizeBallNum(n),
        zodiac: zodiacs[i] != null ? String(zodiacs[i]) : '',
        element: '',
        tone: toneAt(i)
      }))
      const drawTimeFmt = formatDrawDateTime(raw.openTime)
      const issueStr = raw.expect != null ? String(raw.expect) : ''
      let nextDeadline = null
      if (raw.openTime) {
        const ms = parseOpenTimeToMs(raw.openTime)
        if (ms != null) nextDeadline = deadlineFrom(ms)
      }
      return {
        issue: issueStr,
        drawTimeFormatted: drawTimeFmt,
        nextIssueDisplay: issuePlusOne(issueStr),
        nextDrawDeadlineMs: nextDeadline,
        nextIssue: '—',
        nextTime: '—',
        mainNumbers,
        special: {
          value: normalizeBallNum(nums[6]),
          zodiac: zodiacs[6] != null ? String(zodiacs[6]) : '',
          element: '',
          tone: toneAt(6)
        }
      }
    }
  }

  if (
    Array.isArray(raw.nums) &&
    raw.nums.length >= 6 &&
    raw.special != null &&
    raw.special !== ''
  ) {
    const colors = Array.isArray(raw.colorList) ? raw.colorList : []
    const zodiacs = Array.isArray(raw.zodiacList) ? raw.zodiacList : []
    const toneAt = i => normalizeApiTone(colors[i])
    const mainNumbers = raw.nums.slice(0, 6).map((n, i) => ({
      value: normalizeBallNum(n),
      zodiac: zodiacs[i] != null ? String(zodiacs[i]) : '',
      element: '',
      tone: toneAt(i)
    }))
    const drawTimeFmt = formatDrawDateTime(raw.drawTime)
    const issueStr = raw.issueNo != null ? String(raw.issueNo) : String(raw.id ?? '')
    let nextDeadline = null
    if (raw.drawTime) {
      const ms = parseOpenTimeToMs(raw.drawTime)
      if (ms != null) nextDeadline = deadlineFrom(ms)
    }
    return {
      issue: issueStr,
      drawTimeFormatted: drawTimeFmt,
      nextIssueDisplay: issuePlusOne(issueStr),
      nextDrawDeadlineMs: nextDeadline,
      nextIssue: raw.nextIssueNo != null ? String(raw.nextIssueNo) : '—',
      nextTime: raw.nextDrawTime != null ? formatDrawDateTime(raw.nextDrawTime) : '—',
      mainNumbers,
      special: {
        value: normalizeBallNum(raw.special),
        zodiac: zodiacs[6] != null ? String(zodiacs[6]) : '',
        element: '',
        tone: toneAt(6)
      }
    }
  }

  let mains =
    raw.mainNumbers ??
    raw.main_numbers ??
    raw.main ??
    raw.numbers ??
    raw.open_codes ??
    raw.openCode
  if (typeof mains === 'string') {
    mains = mains.split(/[,，\s]+/).filter(Boolean)
  }
  if (!Array.isArray(mains) || mains.length < 6) return null

  let mainArr = mains.slice()
  let sp =
    raw.special ?? raw.specialNumber ?? raw.special_number ?? raw.zodiac_special_num
  if ((sp == null || sp === '') && mainArr.length >= 7) {
    sp = mainArr[6]
    mainArr = mainArr.slice(0, 6)
  }
  if (sp == null || sp === '') return null

  const zodiacs = raw.mainZodiac ?? raw.main_zodiac ?? raw.zodiacs ?? raw.zodiac_main
  const elements = raw.mainElement ?? raw.main_element ?? raw.elements
  const zodiacAll = Array.isArray(raw.zodiac) ? raw.zodiac : null
  const colorList = Array.isArray(raw.colorList) ? raw.colorList : null
  const mainNumbers = mainArr.slice(0, 6).map((n, i) => ({
    value: normalizeBallNum(n),
    zodiac:
      (Array.isArray(zodiacs) && zodiacs[i] != null
        ? String(zodiacs[i])
        : zodiacAll && zodiacAll[i] != null
          ? String(zodiacAll[i])
          : '') || '',
    element: Array.isArray(elements) && elements[i] != null ? String(elements[i]) : '',
    tone: colorList ? normalizeApiTone(colorList[i]) : ''
  }))

  const issueRaw =
    raw.issue ?? raw.issueNo ?? raw.period ?? raw.drawNumber ?? raw.qihao
  const issue =
    issueRaw != null && issueRaw !== ''
      ? typeof issueRaw === 'number'
        ? issueRaw
        : String(issueRaw)
      : 0
  const nextRaw = raw.nextIssue ?? raw.next_period ?? raw.nextDrawNumber
  const nextIssue =
    nextRaw != null && nextRaw !== ''
      ? typeof nextRaw === 'number'
        ? nextRaw
        : String(nextRaw)
      : typeof issue === 'number'
        ? issue + 1
        : '—'
  const nextTime = formatDrawDateTime(
    raw.nextTime ?? raw.next_draw_time ?? raw.nextDrawTime ?? raw.draw_time
  )

  const issueStr =
    issue !== 0 && issue !== ''
      ? typeof issue === 'number'
        ? String(issue)
        : String(issue)
      : ''
  let nextDeadline = null
  const lastOpenSrc = raw.drawTime ?? raw.openTime
  if (lastOpenSrc) {
    const ms = parseOpenTimeToMs(lastOpenSrc)
    if (ms != null) nextDeadline = deadlineFrom(ms)
  }

  return {
    issue,
    nextIssue,
    nextTime: nextTime || formatDrawDateTime(new Date()),
    drawTimeFormatted: raw.drawTime ? formatDrawDateTime(raw.drawTime) : '',
    nextIssueDisplay: issueStr ? issuePlusOne(issueStr) : '—',
    nextDrawDeadlineMs: nextDeadline,
    mainNumbers,
    special: {
      value: normalizeBallNum(sp),
      zodiac: String(
        raw.specialZodiac ??
          raw.special_zodiac ??
          (zodiacAll && zodiacAll[6] != null ? zodiacAll[6] : '') ??
          ''
      ),
      element: raw.specialElement ?? raw.special_element ?? '',
      tone: normalizeApiTone(
        colorList && colorList[6] != null ? colorList[6] : ''
      )
    }
  }
}
